/**
 * Service d'optimisation d'images pour l'app admin
 * Crée automatiquement 3 versions optimisées : thumbnail, medium, high
 * Compatible avec le système d'optimisation de l'app client
 */

import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import { storage } from './firebase';

// Interfaces pour les résultats
export interface OptimizedImageUrls {
  thumbnailUrl: string;    // 300x300
  mediumImageUrl: string;  // 600x600
  highImageUrl: string;    // 1200x1200
}

// When you also want to keep the original (useful for banners)
export interface OptimizedImageUrlsWithOriginal extends OptimizedImageUrls {
  originalUrl: string;
}

export interface ImageOptimizationOptions {
  quality?: number;        // Qualité JPEG (0.1 - 1.0)
  format?: 'webp' | 'jpeg'; // Format de sortie
}

/**
 * Nettoie un nom de fichier en remplaçant les espaces et caractères problématiques
 * Basé sur la fonction cleanFileName du script client
 */
function cleanFileName(fileName: string): string {
  return fileName
    // Remplacer les espaces par des underscores
    .replace(/\s+/g, '_')
    // Remplacer les caractères spéciaux problématiques
    .replace(/[àáâäèéêëìíîïòóôöùúûüç]/gi, (match) => {
      const map: { [key: string]: string } = {
        'à': 'a', 'á': 'a', 'â': 'a', 'ä': 'a',
        'è': 'e', 'é': 'e', 'ê': 'e', 'ë': 'e',
        'ì': 'i', 'í': 'i', 'î': 'i', 'ï': 'i',
        'ò': 'o', 'ó': 'o', 'ô': 'o', 'ö': 'o',
        'ù': 'u', 'ú': 'u', 'û': 'u', 'ü': 'u',
        'ç': 'c'
      };
      return map[match.toLowerCase()] || match;
    })
    // Supprimer les caractères non alphanumériques sauf . _ -
    .replace(/[^a-zA-Z0-9._-]/g, '_')
    // Éviter les underscores multiples
    .replace(/_+/g, '_')
    // Nettoyer le début et la fin
    .replace(/^_+|_+$/g, '');
}

/**
 * Redimensionne une image en utilisant Canvas
 */
function resizeImageToCanvas(
  imageElement: HTMLImageElement,
  targetWidth: number,
  targetHeight: number,
  quality: number = 0.8,
  format: 'webp' | 'jpeg' = 'webp'
): Promise<Blob> {
  return new Promise((resolve, reject) => {
    try {
      const canvas = document.createElement('canvas');
      const ctx = canvas.getContext('2d');
      
      if (!ctx) {
        reject(new Error('Impossible de créer le contexte Canvas'));
        return;
      }

      // Calculer les dimensions en gardant les proportions
      const aspectRatio = imageElement.naturalWidth / imageElement.naturalHeight;
      let canvasWidth = targetWidth;
      let canvasHeight = targetHeight;
      
      if (aspectRatio > 1) {
        // Image plus large que haute
        canvasHeight = canvasWidth / aspectRatio;
      } else {
        // Image plus haute que large
        canvasWidth = canvasHeight * aspectRatio;
      }

      canvas.width = canvasWidth;
      canvas.height = canvasHeight;

      // Dessiner l'image redimensionnée
      ctx.drawImage(imageElement, 0, 0, canvasWidth, canvasHeight);

      // Convertir en blob
      const mimeType = format === 'webp' ? 'image/webp' : 'image/jpeg';
      
      canvas.toBlob(
        (blob) => {
          if (blob) {
            resolve(blob);
          } else {
            reject(new Error('Erreur lors de la conversion en blob'));
          }
        },
        mimeType,
        quality
      );
    } catch (error) {
      reject(error);
    }
  });
}

/**
 * Charge un fichier image en tant qu'élément HTML Image
 */
function loadImageFromFile(file: File): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const img = new Image();
    
    img.onload = () => resolve(img);
    img.onerror = () => reject(new Error('Erreur lors du chargement de l\'image'));
    
    const reader = new FileReader();
    reader.onload = (e) => {
      if (e.target?.result) {
        img.src = e.target.result as string;
      } else {
        reject(new Error('Erreur lors de la lecture du fichier'));
      }
    };
    reader.onerror = () => reject(new Error('Erreur FileReader'));
    reader.readAsDataURL(file);
  });
}

/**
 * Upload un blob vers Firebase Storage et retourne l'URL publique
 */
async function uploadBlobToStorage(
  blob: Blob, 
  storagePath: string
): Promise<string> {
  try {
    const storageRef = ref(storage, storagePath);
    
    // Upload du blob
    await uploadBytes(storageRef, blob);
    
    // Obtenir l'URL de téléchargement
    const downloadURL = await getDownloadURL(storageRef);
    
    return downloadURL;
  } catch (error) {
    console.error(`Erreur upload vers ${storagePath}:`, error);
    throw error;
  }
}

/**
 * Service principal d'optimisation d'images
 */
export const imageOptimizationService = {
  /**
   * Optimise une image générique et upload les versions optimisées dans un dossier spécifique.
   * Exemple: basePath="pub_step_client" pour la bande pub côté Step_Client.
   */
  optimizeImageToBasePath: async (
    basePath: string,
    imageFile: File,
    displayName: string,
    options: ImageOptimizationOptions = {}
  ): Promise<OptimizedImageUrlsWithOriginal> => {
    try {
      const { quality = 0.8, format = 'webp' } = options;

      const safeBasePath = String(basePath || '').replace(/^\/+|\/+$/g, '');
      if (!safeBasePath) {
        throw new Error('basePath requis');
      }

      // 1) Load original
      const imageElement = await loadImageFromFile(imageFile);

      // 2) Compute base filename
      const baseFileName = cleanFileName(displayName || 'image');
      const extension = format === 'webp' ? '.webp' : '.jpg';

      // 3) Create optimized blobs
      const [thumbnailBlob, mediumBlob, highBlob] = await Promise.all([
        resizeImageToCanvas(imageElement, 300, 300, quality, format),
        resizeImageToCanvas(imageElement, 600, 600, quality, format),
        resizeImageToCanvas(imageElement, 1200, 1200, quality, format),
      ]);

      // 4) Upload optimized blobs to basePath
      const [thumbnailUrl, mediumImageUrl, highImageUrl] = await Promise.all([
        uploadBlobToStorage(thumbnailBlob, `${safeBasePath}/${baseFileName}_thumb${extension}`),
        uploadBlobToStorage(mediumBlob, `${safeBasePath}/${baseFileName}_medium${extension}`),
        uploadBlobToStorage(highBlob, `${safeBasePath}/${baseFileName}_high${extension}`),
      ]);

      // 5) Upload original file too (keep original format)
      const originalExt = (imageFile.name.split('.').pop() || 'jpg').toLowerCase();
      const originalStorageRef = ref(storage, `${safeBasePath}/${baseFileName}_orig.${originalExt}`);
      await uploadBytes(originalStorageRef, imageFile);
      const originalUrl = await getDownloadURL(originalStorageRef);

      return { thumbnailUrl, mediumImageUrl, highImageUrl, originalUrl };
    } catch (error) {
      console.error(`❌ Erreur optimisation image (basePath=${basePath}) ${displayName}:`, error);
      throw error;
    }
  },
  /**
   * Optimise une image de produit et upload les 3 versions
   */
  optimizeProductImage: async (
    productId: string,
    imageFile: File,
    productName: string,
    options: ImageOptimizationOptions = {}
  ): Promise<OptimizedImageUrls> => {
    try {
      console.log(`🖼️ Optimisation image produit: ${productName}`);
      
      const { quality = 0.8, format = 'webp' } = options;
      
      // 1. Charger l'image originale
      const imageElement = await loadImageFromFile(imageFile);
      console.log(`📏 Image originale: ${imageElement.naturalWidth}x${imageElement.naturalHeight}`);
      
      // 2. Générer le nom de base nettoyé
      const baseFileName = cleanFileName(productName);
      const extension = format === 'webp' ? '.webp' : '.jpg';
      
      // 3. Créer les 3 versions optimisées
      const [thumbnailBlob, mediumBlob, highBlob] = await Promise.all([
        resizeImageToCanvas(imageElement, 300, 300, quality, format),
        resizeImageToCanvas(imageElement, 600, 600, quality, format),
        resizeImageToCanvas(imageElement, 1200, 1200, quality, format)
      ]);
      
      console.log('✅ 3 versions créées:', {
        thumbnail: `${Math.round(thumbnailBlob.size / 1024)}KB`,
        medium: `${Math.round(mediumBlob.size / 1024)}KB`,
        high: `${Math.round(highBlob.size / 1024)}KB`
      });
      
      // 4. Upload vers Firebase Storage
      const [thumbnailUrl, mediumImageUrl, highImageUrl] = await Promise.all([
        uploadBlobToStorage(thumbnailBlob, `products/${baseFileName}_thumb${extension}`),
        uploadBlobToStorage(mediumBlob, `products/${baseFileName}_medium${extension}`),
        uploadBlobToStorage(highBlob, `products/${baseFileName}_high${extension}`)
      ]);
      
      console.log('🚀 Upload terminé pour:', productName);
      
      return {
        thumbnailUrl,
        mediumImageUrl,
        highImageUrl
      };
      
    } catch (error) {
      console.error(`❌ Erreur optimisation produit ${productName}:`, error);
      throw error;
    }
  },

  /**
   * Optimise une image de catégorie et upload les 3 versions
   */
  optimizeCategoryImage: async (
    categoryId: string,
    imageFile: File,
    categoryName: string,
    options: ImageOptimizationOptions = {}
  ): Promise<OptimizedImageUrls> => {
    try {
      console.log(`📁 Optimisation image catégorie: ${categoryName}`);
      
      const { quality = 0.8, format = 'webp' } = options;
      
      // 1. Charger l'image originale
      const imageElement = await loadImageFromFile(imageFile);
      console.log(`📏 Image originale: ${imageElement.naturalWidth}x${imageElement.naturalHeight}`);
      
      // 2. Générer le nom de base nettoyé
      const baseFileName = cleanFileName(categoryName);
      const extension = format === 'webp' ? '.webp' : '.jpg';
      
      // 3. Créer les 3 versions optimisées
      const [thumbnailBlob, mediumBlob, highBlob] = await Promise.all([
        resizeImageToCanvas(imageElement, 300, 300, quality, format),
        resizeImageToCanvas(imageElement, 600, 600, quality, format),
        resizeImageToCanvas(imageElement, 1200, 1200, quality, format)
      ]);
      
      console.log('✅ 3 versions créées:', {
        thumbnail: `${Math.round(thumbnailBlob.size / 1024)}KB`,
        medium: `${Math.round(mediumBlob.size / 1024)}KB`,
        high: `${Math.round(highBlob.size / 1024)}KB`
      });
      
      // 4. Upload vers Firebase Storage
      const [thumbnailUrl, mediumImageUrl, highImageUrl] = await Promise.all([
        uploadBlobToStorage(thumbnailBlob, `categories/${baseFileName}_thumb${extension}`),
        uploadBlobToStorage(mediumBlob, `categories/${baseFileName}_medium${extension}`),
        uploadBlobToStorage(highBlob, `categories/${baseFileName}_high${extension}`)
      ]);
      
      console.log('🚀 Upload terminé pour:', categoryName);
      
      return {
        thumbnailUrl,
        mediumImageUrl,
        highImageUrl
      };
      
    } catch (error) {
      console.error(`❌ Erreur optimisation catégorie ${categoryName}:`, error);
      throw error;
    }
  },

  /**
   * Vérifie si le navigateur supporte le format WebP
   */
  supportsWebP: (): Promise<boolean> => {
    return new Promise((resolve) => {
      const webP = new Image();
      webP.onload = webP.onerror = () => {
        resolve(webP.height === 2);
      };
      webP.src = 'data:image/webp;base64,UklGRjoAAABXRUJQVlA4IC4AAACyAgCdASoCAAIALmk0mk0iIiIiIgBoSygABc6WWgAA/veff/0PP8bA//LwYAAA';
    });
  },

  /**
   * Obtient les dimensions optimales pour une image
   */
  getOptimalDimensions: (
    originalWidth: number,
    originalHeight: number,
    targetSize: number
  ): { width: number; height: number } => {
    const aspectRatio = originalWidth / originalHeight;
    
    if (aspectRatio > 1) {
      // Image plus large que haute
      return {
        width: targetSize,
        height: Math.round(targetSize / aspectRatio)
      };
    } else {
      // Image plus haute que large
      return {
        width: Math.round(targetSize * aspectRatio),
        height: targetSize
      };
    }
  }
};

export default imageOptimizationService;
